import { useState } from "react";
import { TextField } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import budgetService from "api/budgetService";
import workflowService, {
  WORKFLOW_DECISION_LABELS,
  WorkflowDecisionAction,
} from "api/workflowService";
import acceptanceService from "api/acceptanceService";
import Button from "components/Button";
import HollowButton from "components/HollowButton";
import EngagementDocumentPanel from "components/EngagementDocumentPanel";
import { useEngagementSigning } from "components/EngagementDocumentPanel/useEngagementSigning";
import { formatMoney } from "utils/formatMoney";
import { ENotificationType, notification } from "utils/notification";
import styles from "./styles.module.scss";

interface Props {
  /** Id zaangażowania (DocumentId pozycji skrzynki). */
  engagementId: string;
  /** Id pozycji skrzynki „Otrzymane" — z niej BE odczytuje przydział etapu. */
  itemId?: string;
  /** Po decyzji (np. odświeżenie listy w skrzynce). */
  onDecided?: () => void;
}

const fmtDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString("pl-PL") : "—";

const DECISION_LABELS: Record<string, string> = {
  pending: "oczekuje",
  approved: "zatwierdził(a)",
  rejected: "odrzucił(a)",
  sent_back: "zwrócił(a) do poprawy",
  skipped: "pominięto",
};

const MODE_LABELS: Record<string, string> = {
  any: "dowolna osoba",
  all: "wszyscy",
  two_or_more: "co najmniej dwie osoby",
};

/**
 * Panel prawy skrzynki „Akceptacja i Podpis / Otrzymane" dla zaangażowania
 * budżetowego — port listy „Podpisywanie" (PendingItems) z Magistratu: metryka
 * zaangażowania, przebieg procedury (etapy, kto zdecydował) i moja decyzja na
 * bieżącym etapie: zatwierdź / zwróć do poprawy / odrzuć (z komentarzem).
 */
const EngagementApprovalPanel = ({
  engagementId,
  itemId,
  onDecided,
}: Props) => {
  const {
    data: engagement,
    isLoading,
    error,
  } = budgetService.useGetEngagementForApproval(engagementId);
  const { data: instance } = workflowService.useInstance(
    "engagement",
    engagementId
  );
  const { data: timeline = [] } = acceptanceService.useGetApprovalTimeline(
    "engagement",
    engagementId
  );
  const decide = workflowService.useDecide();

  const [pending, setPending] = useState<WorkflowDecisionAction | null>(null);
  const [comment, setComment] = useState("");
  const [formError, setFormError] = useState("");
  const [showDocument, setShowDocument] = useState(true);

  // Po decyzji BE dopisał etap do dokumentu wniosku — proponujemy podpis XAdES wtyczką
  // (jak „Podpisywanie" w Magistracie). Odświeżenie listy (onDecided) dopiero po podpisie
  // albo pominięciu, bo pozycja znika ze skrzynki „Otrzymane" razem z tym panelem.
  const [signPrompt, setSignPrompt] = useState<string | null>(null);
  const finishSigning = () => {
    setSignPrompt(null);
    onDecided?.();
  };
  const {
    requestSign,
    signing,
    pluginUnavailable,
    modal: signModal,
  } = useEngagementSigning(engagementId, finishSigning);

  const submit = () => {
    if (!pending || decide.isLoading) return;
    if (pending !== "approve" && !comment.trim()) {
      setFormError(
        pending === "reject"
          ? "Podaj powód odrzucenia."
          : "Napisz, co poprawić."
      );
      return;
    }
    setFormError("");
    decide.mutate(
      {
        ItemId: itemId,
        AssignmentId: instance?.MyPendingAssignment?.Id,
        Action: pending,
        Comment: comment.trim() || undefined,
      },
      {
        onSuccess: (res) => {
          notification({
            type: ENotificationType.SUCCESS,
            title: "Zapisano decyzję",
            text: res.DocumentAccepted
              ? "Zaangażowanie zostało zaakceptowane — procedura zakończona."
              : pending === "approve"
              ? "Etap zatwierdzony. Procedura przeszła dalej."
              : pending === "reject"
              ? "Zaangażowanie odrzucone."
              : "Zaangażowanie zwrócone wnioskodawcy do poprawy.",
          });
          setPending(null);
          setComment("");
          if (res.SignatureElementId) setSignPrompt(res.SignatureElementId);
          else onDecided?.();
        },
        onError: (e: unknown) => {
          const msg =
            (
              e as {
                response?: { data?: { errorCodes?: { message: string }[] } };
              }
            )?.response?.data?.errorCodes?.[0]?.message ??
            "Nie udało się zapisać decyzji.";
          setFormError(msg);
        },
      }
    );
  };

  if (isLoading) return <div className={styles.message}>Ładowanie…</div>;
  if (error || !engagement) {
    return (
      <div className={styles.error}>
        Nie udało się wczytać zaangażowania. Możliwe, że nie masz już do niego
        dostępu (etap został rozstrzygnięty).
      </div>
    );
  }

  const actions = instance?.MyAllowedActions ?? [];
  const canDecide = !!instance?.MyPendingAssignment && actions.length > 0;

  return (
    <div className={styles.wrapper}>
      <div className={styles.head}>
        <div className={styles.eyebrow}>Zaangażowanie do akceptacji</div>
        <div className={styles.title}>
          {engagement.Signature ? `${engagement.Signature} · ` : ""}
          {engagement.Title}
        </div>
        <div className={styles.amount}>
          {formatMoney(engagement.Amount ?? 0)} zł
        </div>
      </div>

      <div className={styles.section}>
        <div
          className={`${styles.sectionTitle} ${styles.collapsible}`}
          onClick={() => setShowDocument((v) => !v)}
        >
          <span>Dokument wniosku</span>
          <FontAwesomeIcon
            icon={showDocument ? faChevronDown : faChevronRight}
            className={styles.chevron}
          />
        </div>
        {showDocument && (
          <EngagementDocumentPanel
            engagementId={engagementId}
            previewHeight={440}
            // Ten sam etap da się podpisać też przyciskiem w panelu dokumentu — wtedy
            // domykamy prompt „Podpis elektroniczny etapu" (inaczej zostałby widoczny, a jego
            // przycisk padłby na „element nie czeka na Twój podpis").
            onSigned={() => {
              if (signPrompt) finishSigning();
            }}
          />
        )}
      </div>

      <dl className={styles.meta}>
        <dt>Wydatek</dt>
        <dd>{engagement.ExpenseTitle || "—"}</dd>
        <dt>Klasyfikacja</dt>
        <dd>
          {[engagement.DivisionSymbol, engagement.ChapterSymbol]
            .filter(Boolean)
            .join(" / ") || "—"}
          {engagement.ChapterName ? ` — ${engagement.ChapterName}` : ""}
        </dd>
        <dt>Wnioskodawca</dt>
        <dd>{engagement.CreatedBy || instance?.RequesterLabel || "—"}</dd>
        <dt>Data zaangażowania</dt>
        <dd>{fmtDate(engagement.EngagementDate)}</dd>
        <dt>Termin realizacji</dt>
        <dd>{fmtDate(engagement.ExecutionDeadline)}</dd>
        <dt>Tryb udzielenia</dt>
        <dd>
          {engagement.GrantingProcedure || "—"}
          {engagement.GrantingProcedureDescription
            ? ` — ${engagement.GrantingProcedureDescription}`
            : ""}
        </dd>
        {engagement.Justification && (
          <>
            <dt>Uzasadnienie</dt>
            <dd>{engagement.Justification}</dd>
          </>
        )}
      </dl>

      {engagement.Quotas?.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Kwoty wg paragrafów</div>
          <table className={styles.quotas}>
            <tbody>
              {engagement.Quotas.map((q) => (
                <tr key={q.Id ?? q.ExpenseQuotaId}>
                  <td>{q.ParagraphSymbol ?? q.ExpenseQuotaId}</td>
                  <td className={styles.num}>{formatMoney(q.Amount)} zł</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {instance && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            Procedura: {instance.TemplateName || "workflow"}
            <span className={`${styles.pill} ${styles[instance.Status]}`}>
              {instance.Status === "active"
                ? "w toku"
                : instance.Status === "completed"
                ? "zakończona"
                : instance.Status === "rejected"
                ? "odrzucona"
                : instance.Status === "returned"
                ? "zwrócona do poprawy"
                : "anulowana"}
            </span>
          </div>
          <ol className={styles.steps}>
            {instance.Steps.map((s) => (
              <li key={s.Index} className={styles[`step_${s.State}`]}>
                <div className={styles.stepName}>
                  {s.Name || `Etap ${s.Index + 1}`}
                  <span className={styles.stepMode}>
                    {MODE_LABELS[s.ApprovalMode] ?? s.ApprovalMode}
                  </span>
                </div>
                {s.Assignments.length > 0 ? (
                  <ul className={styles.assignments}>
                    {s.Assignments.map((a) => (
                      <li key={a.Id}>
                        <span className={styles.who}>
                          {a.AssigneeLabel ?? "—"}
                          {a.IsMine ? " (ja)" : ""}
                        </span>
                        <span
                          className={`${styles.decision} ${
                            styles[`dec_${a.Decision}`]
                          }`}
                        >
                          {DECISION_LABELS[a.Decision] ?? a.Decision}
                        </span>
                        {a.Comment && (
                          <span className={styles.comment}>„{a.Comment}”</span>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  s.State === "upcoming" && (
                    <div className={styles.upcoming}>
                      osoby zostaną wyznaczone po poprzednim etapie
                    </div>
                  )
                )}
              </li>
            ))}
          </ol>
        </div>
      )}

      {timeline.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Historia</div>
          <ul className={styles.timeline}>
            {timeline.map((ev, i) => (
              <li key={i}>
                <span className={styles.tlWhen}>
                  {new Date(ev.when).toLocaleString("pl-PL", {
                    dateStyle: "short",
                    timeStyle: "short",
                  })}
                </span>
                <span>
                  {ev.who}{" "}
                  {ev.action === "submit"
                    ? "wysłał(a) do akceptacji"
                    : ev.action === "approve"
                    ? "zatwierdził(a)"
                    : ev.action === "reject"
                    ? "odrzucił(a)"
                    : ev.action === "sign"
                    ? "podpisał(a) elektronicznie"
                    : "zwrócił(a) do poprawy"}
                  {ev.stepName ? ` — ${ev.stepName}` : ""}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {signPrompt && (
        <div className={styles.decide}>
          <div className={styles.sectionTitle}>Podpis elektroniczny etapu</div>
          <div className={styles.info}>
            Decyzja została zapisana w dokumencie wniosku z podpisem tekstowym.
            Możesz dołożyć podpis elektroniczny certyfikatem (wtyczka Nota).
          </div>
          {pluginUnavailable && (
            <div className={styles.formError}>
              Wtyczka Nota jest niedostępna — podpis elektroniczny pominięty.
            </div>
          )}
          <div className={styles.actions}>
            <Button
              text="Podpisz elektronicznie"
              loading={signing}
              disabled={pluginUnavailable}
              onClick={() => requestSign(signPrompt)}
            />
            <HollowButton text="Pomiń" onClick={finishSigning} />
          </div>
        </div>
      )}

      {signModal}

      {canDecide && !signPrompt && (
        <div className={styles.decide}>
          <div className={styles.sectionTitle}>Twoja decyzja</div>
          {!pending ? (
            <div className={styles.actions}>
              {actions.includes("approve") && (
                <Button
                  text={WORKFLOW_DECISION_LABELS.approve}
                  onClick={() => setPending("approve")}
                />
              )}
              {actions.includes("send_back") && (
                <HollowButton
                  text={WORKFLOW_DECISION_LABELS.send_back}
                  onClick={() => setPending("send_back")}
                />
              )}
              {actions.includes("reject") && (
                <HollowButton
                  text={WORKFLOW_DECISION_LABELS.reject}
                  onClick={() => setPending("reject")}
                />
              )}
            </div>
          ) : (
            <div className={styles.decideForm}>
              <div className={styles.decideLabel}>
                {WORKFLOW_DECISION_LABELS[pending]}
              </div>
              <TextField
                size="small"
                label={
                  pending === "approve"
                    ? "Komentarz (opcjonalnie)"
                    : "Uzasadnienie"
                }
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                multiline
                minRows={2}
                fullWidth
              />
              {formError && <div className={styles.formError}>{formError}</div>}
              <div className={styles.actions}>
                <Button
                  text="Potwierdź"
                  onClick={submit}
                  loading={decide.isLoading}
                />
                <HollowButton
                  text="Anuluj"
                  onClick={() => {
                    setPending(null);
                    setComment("");
                    setFormError("");
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {!canDecide && instance?.Status === "active" && (
        <div className={styles.info}>Ten etap nie czeka na Twoją decyzję.</div>
      )}
    </div>
  );
};

export default EngagementApprovalPanel;
